// The public Protection page (website build brief section 8): a plain
// explanation first, then the exact scheme, the deployment, the upgrade
// authority, the reviews and the audit status, recovery, what stays outside
// the protection, and where the evidence is. Every figure comes from
// facts.ts, which records when it was last checked. The intro (design report
// 2026-09-29, P2) carries a restrained crop of the aperture and a three-part
// summary, covered / outside / status, before the deep sections. The status
// chips, the three steps (you authorize, the program enforces, your vault
// holds the result) and the practical questions came here from the home page
// when it became the quiet landing (app/art/vault-chip/HANDOFF.md M1).

import { Link } from 'wouter';
import { FEE_RESERVE_DISCLOSURE } from '../components/GasTank';
import detail640 from './art/horizon-detail-640.webp';
import detail1040 from './art/horizon-detail-1040.webp';
import { ArrowRight, ExternalLink } from './chrome';
import {
  ARTIFACT_BYTES,
  ARTIFACT_SHA256,
  FACTS_CHECKED,
  FEE_PERCENT,
  FIRST_DEPLOYED,
  LAST_UPGRADE_SLOT,
  LAST_UPGRADED,
  NETWORK,
  ONE_TIME_KEYS_PER_TREE,
  PROGRAM_DATA,
  PROGRAM_ID,
  REVIEWS,
  UPGRADE_AUTHORITY,
  explorerAddress,
} from './facts';
import { useHashLanding } from './motion';
import { SitePage } from './SitePage';

const TOC: Array<{ id: string; label: string }> = [
  { id: 'plain', label: 'In plain terms' },
  { id: 'features', label: 'What you can do' },
  { id: 'scheme', label: 'The signature scheme' },
  { id: 'deployment', label: 'The deployment' },
  { id: 'authority', label: 'Upgrade authority' },
  { id: 'reviews', label: 'Reviews and audit' },
  { id: 'recovery', label: 'Recovery' },
  { id: 'outside', label: 'What stays outside' },
  { id: 'questions', label: 'Questions' },
  { id: 'evidence', label: 'Evidence' },
];

const STATUS: Array<{ label: string; tone: 'ok' | 'warn' | 'plain' }> = [
  { label: 'Live on Solana mainnet, as an experiment', tone: 'ok' },
  { label: 'Staking live on mainnet', tone: 'ok' },
  { label: 'External audit pending', tone: 'warn' },
  { label: 'Program upgradeable by one key', tone: 'warn' },
  { label: 'No privacy: activity is public', tone: 'plain' },
];

const QUESTIONS: Array<{ q: string; a: JSX.Element }> = [
  {
    q: 'What does quantum-resistant mean here?',
    a: (
      <p>
        Spending from your vault needs a hash-based, one-time signature (a generalized XMSS scheme over Keccak-256), which rests on hash functions rather than elliptic-curve math. It covers the vault&rsquo;s spending authority,
        not everything around it; the design targets 128-bit classical and 64-bit quantum security, with no NIST level claimed. <a href="#plain">What it covers</a>
      </p>
    ),
  },
  {
    q: 'What can I do today?',
    a: (
      <p>
        Create or restore a vault, deposit, send to any Solana address, swap through supported Jupiter routes inside the vault, and stake SOL from it, with a validator or into a liquid staking token such as JitoSOL. It
        is an experiment on Solana mainnet ahead of its external audit: use only amounts you can afford to lose, and expect some tokens and routes not to be supported.
      </p>
    ),
  },
  {
    q: 'Is it audited, and can the program be upgraded?',
    a: (
      <p>
        Not yet audited. Every stage passed a review gate before it shipped, and an external audit comes before any public beta. The program can be upgraded, today by one key; the intention is a 2-of-3 multisig before any
        public beta and, only after an external audit and a bounty period, no upgrade authority at all. No date is promised.
      </p>
    ),
  },
  {
    q: 'How do I recover on another device?',
    a: (
      <p>
        Choose Restore and enter your 24 words; the app finds the same vault and the same fee reserve, with its SOL. Before ordinary use it refreshes the vault&rsquo;s key, one small transaction paid from that fee reserve;
        add a little SOL first only if it does not hold enough for it. Use one device at a time; the device passphrase only unlocks that device.{' '}
        {/* docs/18 section 6, a legacy device's older key: the overview's notice moves its SOL into the fee reserve */}
        A device set up before this release also holds an older per-device fee reserve key that your 24 words cannot recover: move its SOL into your fee reserve from the overview before that device is forgotten or its
        browser data is cleared.
      </p>
    ),
  },
  {
    q: 'Why is a network fee reserve needed?',
    a: (
      <p>
        Every Solana transaction needs a fee payer that signs with an ordinary key, and your vault has none. A small SOL balance in a separate fee key pays network fees and account rent. It cannot move your vault&rsquo;s
        funds.
      </p>
    ),
  },
  {
    q: 'What happens if that reserve is empty?',
    a: (
      <p>
        Vault actions wait until you add SOL to it; your holdings are still there. Before it uses a one-time key, the app checks that the reserve covers the action&rsquo;s network fee and any account rent, and stops if it
        does not, so no key is spent on an attempt the reserve cannot pay for.
      </p>
    ),
  },
  {
    q: 'Does every incoming transfer fund the reserve?',
    a: <p>No. SOL sent to your vault&rsquo;s address stays in the vault. The reserve has its own address, which you fund separately; a single deposit that funds both is under consideration, not built.</p>,
  },
  {
    q: 'How does staking work?',
    a: (
      <p>
        Stake SOL with a validator straight from your vault: the stake account&rsquo;s only withdraw authority is your vault, and Restore finds it from your vault&rsquo;s address alone. Or swap into a liquid staking token
        such as JitoSOL, which stays in your vault. Rewards vary and are not promised; the validator and a token&rsquo;s stake pool are outside your vault&rsquo;s protection.
      </p>
    ),
  },
  {
    q: 'Can I send assets to a normal wallet?',
    a: <p>Yes, to any Solana address, with no protocol fee. Once they arrive, that wallet&rsquo;s own key controls them, not your vault.</p>,
  },
  {
    q: 'Are my holdings or transactions private?',
    a: <p>No. Your vault&rsquo;s address, balances and every transaction, including swap routes and amounts, are public on Solana.</p>,
  },
  {
    q: 'What does it cost?',
    a: (
      <p>
        A {FEE_PERCENT} swap fee, taken in USDC or USDT when a swap has either, otherwise in SOL, otherwise from the output, and shown on its own line before you confirm. Deposits, sends and key refreshes carry no protocol
        fee; network fees and account rent come from your fee reserve.
      </p>
    ),
  },
];

function Address({ value, what }: { value: string; what: string }) {
  return (
    <>
      <span className="mono">{value}</span>
      <small>
        <ExternalLink href={explorerAddress(value)} className="explorer-link">
          View <span className="sr-only">the {what} </span>on Solana Explorer
        </ExternalLink>
      </small>
    </>
  );
}

export function Protection({ launchHref }: { launchHref: string }) {
  useHashLanding();
  return (
    <SitePage
      launchHref={launchHref}
      className="protection-page"
      title="Docs · ZeroQube"
      description="How ZeroQube works: the quantum-resistant spending key, what the program enforces, staking, recovery, the deployment and its upgrade authority, the reviews, and the limits."
    >
      <header className="page-hero">
        <div className="page-hero-img" aria-hidden="true">
          <img src={detail640} srcSet={`${detail640} 640w, ${detail1040} 1040w`} sizes="(max-width: 900px) 100vw, 44vw" alt="" width={640} height={800} decoding="async" />
        </div>
        <div className="container">
          <p className="eyebrow">Docs</p>
          <h1 className="page-title">What protects your vault, and what does not.</h1>
          <p className="lede">
            ZeroQube is a Solana vault with a quantum-resistant spending key: every spend needs a hash-based, one-time signature, and the program checks it on chain. Hold, send, swap and stake from the same vault, live on
            Solana mainnet today as an experiment. Here is exactly what that covers, how mature it is, and what still depends on someone else.
          </p>
          <p className="page-meta">
            <span>Status checked {FACTS_CHECKED}.</span>
          </p>
          <ul className="status-chips" aria-label="Status at a glance">
            {STATUS.map((s) => (
              <li key={s.label} className={`pill pill-${s.tone}`}>
                {s.label}
              </li>
            ))}
          </ul>
          <div className="summary-grid" role="list" aria-label="Summary">
            <div role="listitem" className="summary-card summary-covers">
              <h2>What it covers</h2>
              <p>Who can spend from your vault, and within which limits. Under the deployed program, every spend needs the vault&rsquo;s one-time, hash-based signature; a swap&rsquo;s most-spent and least-received amounts are enforced by the program.</p>
              <a href="#plain" className="link-arrow">
                In plain terms
                <ArrowRight />
              </a>
            </div>
            <div role="listitem" className="summary-card summary-outside">
              <h2>What remains outside</h2>
              <p>The ordinary key that pays network fees, the program&rsquo;s upgrade key, Solana itself, token programs and swap venues, your device and your recovery phrase. Nothing here is private.</p>
              <a href="#outside" className="link-arrow">
                What stays outside
                <ArrowRight />
              </a>
            </div>
            <div role="listitem" className="summary-card summary-status">
              <h2>Current status</h2>
              <p>Live on Solana mainnet since 24 September 2026, with staking since 10 October. Upgradeable by one key today. No external audit yet; every stage passed a review gate before it shipped.</p>
              <a href="#reviews" className="link-arrow">
                Reviews and audit
                <ArrowRight />
              </a>
            </div>
          </div>
        </div>
      </header>

      <div className="container doc">
        <nav className="doc-toc" aria-label="On this page">
          <h2>On this page</h2>
          {TOC.map((t) => (
            <a key={t.id} href={`#${t.id}`}>
              {t.label}
            </a>
          ))}
        </nav>

        <div className="doc-body">
          <section id="plain" className="doc-section" aria-labelledby="plain-title">
            <h2 id="plain-title">In plain terms</h2>
            <p>
              Your vault is an account on Solana with no private key. Under the deployed program, the only way to move assets out of it is a signature from your vault&rsquo;s current key, and that key is{' '}
              <strong>hash-based</strong>: it rests on hash functions, not on the elliptic-curve math that a large quantum computer is expected to break. The program&rsquo;s upgrade authority is the exception, described below.
            </p>
            <p>
              Each key signs once. Your device makes them from your 24-word recovery phrase, and the vault moves to a fresh set automatically as they are used. Your vault keeps the same address.
            </p>
            <ol className="steps-list">
              <li>
                <strong>You authorize</strong>
                <span>A one-time, hash-based signature from your device, over exact limits.</span>
              </li>
              <li>
                <strong>The program enforces</strong>
                <span>It checks the signature and the limits. A supported swap route runs outside the vault, within them.</span>
              </li>
              <li>
                <strong>Your vault holds the result</strong>
                <span>The output lands in accounts your vault owns: at least your minimum, or nothing moves.</span>
              </li>
            </ol>
            <ul className="plain-points">
              <li>
                <strong>Covered:</strong> who can spend from the vault, and within which limits. A swap&rsquo;s most-spent and least-received amounts are enforced by the program itself.
              </li>
              <li>
                <strong>Not covered:</strong> the ordinary key that pays network fees, the key that can upgrade the program, Solana itself, the token programs and swap venues, your device, your recovery phrase, and
                privacy.
              </li>
              <li>
                <strong>Not yet:</strong> an external audit. Every stage so far passed a review gate before it shipped, which is not a substitute.
              </li>
            </ul>
          </section>

          <section id="features" className="doc-section" aria-labelledby="features-title">
            <h2 id="features-title">What you can do</h2>
            <ul className="plain-points">
              <li>
                <strong>Hold.</strong> SOL and tokens sit in accounts your vault owns; under the deployed program, moving them needs your vault&rsquo;s one-time signature.
              </li>
              <li>
                <strong>Send.</strong> To any Solana address, with no protocol fee.
              </li>
              <li>
                <strong>Swap.</strong> Through supported Jupiter routes, from inside the vault: the program enforces the most you spend and the least you receive, or nothing moves. A {FEE_PERCENT} fee, shown before you confirm.
              </li>
              <li>
                <strong>Stake.</strong> SOL with a validator, straight from the vault, or into a liquid staking token such as JitoSOL, held in the vault. Proved on mainnet; rewards vary.
              </li>
              <li>
                <strong>Recover.</strong> Your 24 words restore the vault, its fee reserve and its stake accounts on any device.
              </li>
            </ul>
          </section>

          <section id="scheme" className="doc-section" aria-labelledby="scheme-title">
            <h2 id="scheme-title">The signature scheme</h2>
            <dl className="spec">
              <div>
                <dt>Family</dt>
                <dd>
                  Hash-based, stateful: generalized XMSS (DKKW25) over target-sum Winternitz one-time signatures
                  <small>Not the NIST-standardized XMSS profile.</small>
                </dd>
              </div>
              <div>
                <dt>Hash function</dt>
                <dd>Keccak-256</dd>
              </div>
              <div>
                <dt>One-time keys</dt>
                <dd>
                  {ONE_TIME_KEYS_PER_TREE} per key tree; each signs exactly once
                  <small>The vault installs the next tree automatically after about 200 are used.</small>
                </dd>
              </div>
              <div>
                <dt>Sizes</dt>
                <dd>1,037-byte signature, 41-byte verifying key</dd>
              </div>
              <div>
                <dt>Security target</dt>
                <dd>
                  128-bit classical, 64-bit quantum
                  <small>The scheme&rsquo;s own target. No NIST security level is claimed.</small>
                </dd>
              </div>
              <div>
                <dt>Expiry</dt>
                <dd>
                  Every swap or send signature expires after about 60 seconds
                  <small>The one fixed key-refresh message used in recovery has no expiry by design, so every restore signs the same bytes.</small>
                </dd>
              </div>
              <div>
                <dt>Verifier</dt>
                <dd>
                  The solana-winternitz library, pinned to one version that the project reviewed itself
                  <small>Recent, with no track record and no third-party audit; a defect in it would be a total break.</small>
                </dd>
              </div>
            </dl>
            <p>
              A signature binds the vault, the key generation, the program deployment and the operation&rsquo;s limits (the exact route too, unless you choose a bounded route). Reusing a one-time key for two different messages
              would break it, which is why the app retires a key whenever an attempt may have exposed it, and why one device at a time is the supported model.
            </p>
          </section>

          <section id="deployment" className="doc-section" aria-labelledby="deployment-title">
            <h2 id="deployment-title">The deployment</h2>
            <dl className="spec">
              <div>
                <dt>Network</dt>
                <dd>{NETWORK}</dd>
              </div>
              <div>
                <dt>Program</dt>
                <dd>
                  <Address value={PROGRAM_ID} what="program" />
                </dd>
              </div>
              <div>
                <dt>History</dt>
                <dd>
                  Deployed {FIRST_DEPLOYED}; upgraded {LAST_UPGRADED} at slot {LAST_UPGRADE_SLOT}
                </dd>
              </div>
              <div>
                <dt>Build</dt>
                <dd>
                  {ARTIFACT_BYTES} bytes, sha256
                  <span className="mono">{ARTIFACT_SHA256}</span>
                  <small>The program data account is larger (84,592 bytes): the hash covers the first {ARTIFACT_BYTES} and the rest is zero.</small>
                </dd>
              </div>
              <div>
                <dt>Program data</dt>
                <dd>
                  <Address value={PROGRAM_DATA} what="program data account" />
                </dd>
              </div>
              <div>
                <dt>Swap fee</dt>
                <dd>
                  {FEE_PERCENT} of each settled swap, set in the program
                  <small>No protocol fee on deposits, sends or key refreshes.</small>
                </dd>
              </div>
            </dl>
            <p>
              Before every vault action, the app reads the deployed program through your RPC endpoint and refuses to sign if its hash differs from the published one, unless you switch on the session-only development option in
              Settings. It cannot protect you from an upgrade authority that has already been compromised. You can run the same comparison yourself, without unlocking anything.
            </p>
            <div className="link-row">
              <Link href="/verify" className="btn btn-secondary">
                Verify the deployment
                <ArrowRight />
              </Link>
            </div>
          </section>

          <section id="authority" className="doc-section" aria-labelledby="authority-title">
            <h2 id="authority-title">Upgrade authority</h2>
            <div className="callout" role="note">
              <strong>The program can be upgraded.</strong>
              <span>
                Today a single key holds the upgrade authority. Until that authority is removed, whoever holds it could replace the program, so the vault is only as quantum-resistant as that key.
              </span>
            </div>
            <dl className="spec">
              <div>
                <dt>Current authority</dt>
                <dd>
                  <Address value={UPGRADE_AUTHORITY} what="upgrade authority" />
                  <small>One Ed25519 key. Not a multisig.</small>
                </dd>
              </div>
            </dl>
            <ol className="steps-list">
              <li>
                <strong>Before any public beta</strong>
                <span>Move the authority to a 2-of-3 multisig and publish a reproducible build of the program.</span>
              </li>
              <li>
                <strong>After an external audit, a bounty period and a complete allow-list</strong>
                <span>Remove the upgrade authority, so the program can never change again, and publish the final build. No date is promised.</span>
              </li>
            </ol>
            <p>
              Once the authority is removed nothing can be added or fixed: a change in a swap route format or a new token feature would need a new program, with new vault addresses, and vaults would move to it by hand.
            </p>
          </section>

          <section id="reviews" className="doc-section" aria-labelledby="reviews-title">
            <h2 id="reviews-title">Reviews and audit</h2>
            <div className="callout" role="note">
              <strong>No external audit yet.</strong>
              <span>An external program audit and a cryptographic review have not started. They are required before any public beta.</span>
            </div>
            <p>
              Every stage so far closed only after a gate review by a separate AI reviewer, which checks the design, the code and the test evidence before anything ships. From a first assessment through the staking release
              and the shared fee reserve, it has recorded 95 numbered findings, and each gate closed only once its blocking findings were fixed. These reviews are not a substitute for a human audit.
            </p>
            <table className="review-table">
              <caption>Assessment and gate reviews, 2026.</caption>
              <thead>
                <tr>
                  <th scope="col">Gate</th>
                  <th scope="col">Date</th>
                  <th scope="col">Scope</th>
                </tr>
              </thead>
              <tbody>
                {REVIEWS.map((r) => (
                  <tr key={r.gate}>
                    <th scope="row">{r.gate}</th>
                    <td>{r.date}</td>
                    <td>{r.scope}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>
              Early gates reviewed source and evidence; later rounds also ran the test suites, and at G6 the reviewer rebuilt the program binary. None is a fuzzing campaign, a proof or a professional audit.
            </p>
            <p>
              The deployed build is the staking release, upgraded on {LAST_UPGRADED} after the staking reviews and the review of the upgrade procedure.
            </p>
          </section>

          <section id="recovery" className="doc-section" aria-labelledby="recovery-title">
            <h2 id="recovery-title">Recovery</h2>
            <ul className="plain-points">
              <li>
                <strong>Your 24 words are the vault.</strong> They are the only way to restore it. Anyone who has them can spend from it, and if you lose them no one can recover it for you.
              </li>
              <li>
                <strong>The device passphrase only protects this device.</strong> It seals a copy of your key material in this browser. It cannot recover the vault anywhere else.
              </li>
              <li>
                <strong>Restoring starts with a key refresh.</strong> A new device cannot know which one-time keys another device may have exposed, so before ordinary use it moves the vault to a fresh key tree: one small
                transaction, paid from your fee reserve. If the reserve does not hold enough for it, fund it first; until the refresh lands the restored vault cannot be used, and your funds are not affected.
              </li>
              <li>
                <strong>The fee reserve is derived from your 24 words.</strong> Every device that opens your vault, and every restore, finds the same fee reserve and its SOL; a lost device or cleared browser data does not lose
                it, and the device passphrase cannot change it.{' '}
                {/* docs/18 section 6, a legacy device's older key: the overview's notice moves its SOL into the fee reserve */}
                A device set up before this release also holds an older per-device fee reserve key that your 24 words cannot recover: move its SOL into your fee reserve from the overview before that device is forgotten or its
                browser data is cleared.
              </li>
              <li>
                <strong>One device at a time.</strong> Using two devices at once can reuse a one-time key.
              </li>
            </ul>
          </section>

          <section id="outside" className="doc-section" aria-labelledby="outside-title">
            <h2 id="outside-title">What stays outside</h2>
            <ul className="plain-points">
              <li>
                <strong>The fee key.</strong> Every transaction is paid for and signed by an ordinary Ed25519 key holding your fee reserve. It cannot spend vault funds, but it is not quantum-resistant, and it could delay your
                transactions. {/* the compromise disclosure, docs/18 section 7's reviewed text (one of its four places) */}
                {FEE_RESERVE_DISCLOSURE}
              </li>
              <li>
                <strong>The upgrade authority.</strong> Described above: one key today.
              </li>
              <li>
                <strong>Solana itself.</strong> Validators and consensus use Ed25519 keys.
              </li>
              <li>
                <strong>Programs the vault calls.</strong> Jupiter, the swap venues and the token programs are outside the vault. The vault bounds what a swap may take and must return, but it cannot fix a program that misbehaves.
              </li>
              <li>
                <strong>Staking.</strong> A stake account&rsquo;s only withdraw authority is your vault, but the validator you choose, and a liquid staking token&rsquo;s pool, its program and its operators, are outside your
                vault&rsquo;s protection. Rewards vary and are not promised.
              </li>
              <li>
                <strong>Token issuers.</strong> A token&rsquo;s own rules still apply inside a vault: an issuer&rsquo;s power to freeze accounts, or a Token-2022 permanent delegate that can move tokens without your vault&rsquo;s
                signature.
              </li>
              <li>
                <strong>Your device and your phrase.</strong> A compromised browser can sign while the vault is unlocked, and anyone with your 24 words owns the vault.
              </li>
              <li>
                <strong>Privacy.</strong> None. Your vault address, balances and every transaction, including swap routes and amounts, are public.
              </li>
              <li>
                <strong>Assets you send away.</strong> Once assets reach an ordinary wallet, that wallet&rsquo;s key controls them, not your vault.
              </li>
            </ul>
          </section>

          <section id="questions" className="doc-section" aria-labelledby="questions-title">
            <h2 id="questions-title">Questions</h2>
            <div className="faq-list">
              {QUESTIONS.map((item) => (
                <details key={item.q} className="faq-item">
                  <summary>
                    <span>{item.q}</span>
                    <span className="faq-icon" aria-hidden="true" />
                  </summary>
                  <div className="faq-answer">{item.a}</div>
                </details>
              ))}
            </div>
          </section>

          <section id="evidence" className="doc-section" aria-labelledby="evidence-title">
            <h2 id="evidence-title">Evidence</h2>
            <p>
              What you can check today: the deployed program against its published hash on the <Link href="/verify">verify page</Link>, and every address above on a public explorer.
            </p>
            <p>
              The repository (the program source, the review records and the evidence files) is private until the external audit, and will be published with the audit reports before any public beta.
            </p>
          </section>
        </div>
      </div>
    </SitePage>
  );
}
